import type { FetcherBaseProps, FetcherResponse } from 'fetch-toolkit/types'
import { sendError } from 'monitoring/sendError/server/sendError'
import { env } from 'utils/envManager'
import { stringToBoolean } from 'utils/stringToBoolean/stringToBoolean'

const DEFAULT_TIMEOUT = 3000

export const fetcher = async <T>({
	url,
	searchParams,
	requestConfig,
	errorConfig,
	isThrowError,
}: FetcherBaseProps): Promise<FetcherResponse<T | string> | undefined> => {
	const {
		method,
		timeout = DEFAULT_TIMEOUT,
		revalidate,
		...fetchConfig
	} = requestConfig

	const { domain, message, custom } = errorConfig

	// Check if Vercel is enabled
	const isVercelEnabled = stringToBoolean(env.VERCEL_ACTIVATED)

	const controller = new AbortController()
	const timeoutId = setTimeout(() => controller.abort(), timeout)

	// Add search params
	if (searchParams) {
		url.search = searchParams.toString()
	}

	const handleError = (error?: unknown) => {
		if (isThrowError) {
			throw sendError({
				domain,
				message,
				custom: {
					error,
					custom,
				},
			})
		}

		console.error({
			domain,
			message,
			custom: {
				error,
				custom,
			},
		})
	}

	try {
		// eslint-disable-next-line mng/fetch-always-revalidate-with-zero
		const response = await fetch(url, {
			...fetchConfig,
			method: requestConfig.method,
			signal: controller.signal,
			next: {
				revalidate: isVercelEnabled ? revalidate : 0,
			},
		})

		if (timeoutId) {
			clearTimeout(timeoutId)
		}

		const payloadResponse = {
			status: response.status,
			headers: response.headers,
			ok: response.ok,
		}

		let data
		const errorResponse = response.clone()

		try {
			data = await response.json()
		} catch {
			data = await errorResponse.text()
		}

		if (!response.ok) {
			handleError(data)

			return {
				data,
				...payloadResponse,
			}
		}

		return {
			data,
			...payloadResponse,
		}
	} catch (error) {
		handleError(error)

		return undefined
	}
}
